import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api } from '../lib/api';
import { banfNr } from './SammelZettel';
import { NeuEtikett, useEinkaufNeu } from '../components/EinkaufNeu';

// Bestellanforderungen (BANF, Einkauf): Liste + „Neue BANF".
// Eine BANF = DV-Nr (Kontierung / unsere interne Bestellnummer) + freie Positionen (Schrauben, Dichtungen, Nabe …).
// Intern heißt die Tabelle weiterhin `sammelzettel` (nur die Oberfläche spricht SAP-Deutsch).

function fmtDatum(v: string | null): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}
const FILTER = [['offen', 'Offen'], ['erledigt', 'Erledigt'], ['storniert', 'Storniert'], ['alle', 'Alle']] as const;

export function SammelBestellung() {
  const nav = useNavigate();
  const [status, setStatus] = useState<string>('offen');
  const [q, setQ] = useState('');
  const liste = useApi(() => api.sammelzettel.list(status, q.trim()), [status, q]);
  const { daten: neuDaten } = useEinkaufNeu();
  const neuZettel = new Set(neuDaten.banf.map(p => p.zettel_id));
  const [neu, setNeu] = useState(false);
  const [f, setF] = useState({ dv_nummer: '', bezeichnung: '', notiz: '' });
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  async function anlegen() {
    setBusy(true); setFehler('');
    try {
      const r = await api.sammelzettel.create({ dv_nummer: f.dv_nummer, bezeichnung: f.bezeichnung, notiz: f.notiz || undefined });
      nav(`/banf/${r.data.id}`);
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }

  const zeilen = liste.data?.data ?? [];
  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
          <h1 className="text-2xl font-bold text-slate-900">📋 Bestellanforderungen (BANF)</h1>
          <p className="text-sm text-slate-600 mt-1">
            Eine BANF je DV (Kontierung): beliebige Positionen eintragen oder aus dem Artikelstamm wählen, dann je Lieferant die Bestellung anlegen.
          </p>
        </div>
        <button data-testid="neuer-zettel" onClick={() => setNeu(n => !n)}
                className="bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 shadow-sm">➕ Neue BANF</button>
      </header>

      {neu && (
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="neu-form">
          <div className="grid md:grid-cols-3 gap-2">
            <input className="input" aria-label="DV-Nr" placeholder="DV-Nr * (unsere Bestell-Nr)" value={f.dv_nummer} onChange={e => setF({ ...f, dv_nummer: e.target.value })} />
            <input className="input md:col-span-2" aria-label="Bezeichnung" placeholder="Bezeichnung * (z. B. Ventil XY komplett)" value={f.bezeichnung} onChange={e => setF({ ...f, bezeichnung: e.target.value })} />
            <input className="input md:col-span-3" aria-label="Notiz" placeholder="Notiz (optional)" value={f.notiz} onChange={e => setF({ ...f, notiz: e.target.value })} />
          </div>
          {fehler && <div className="text-sm text-red-600 mt-2">{fehler}</div>}
          <div className="mt-3 flex gap-2">
            <button className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-50" disabled={busy} onClick={anlegen}>BANF anlegen</button>
            <button className="btn-secondary" onClick={() => setNeu(false)}>Abbrechen</button>
          </div>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex gap-1">
          {FILTER.map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)}
                    className={`px-3 py-1.5 rounded-full text-sm ${status === k ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{l}</button>
          ))}
        </div>
        <input className="input w-64" placeholder="🔍 DV oder Bezeichnung …" aria-label="BANF suchen" value={q} onChange={e => setQ(e.target.value)} />
        <span className="text-sm text-slate-500">{liste.loading ? 'Lädt …' : `${zeilen.length} BANF`}</span>
      </div>
      {liste.error && <div className="mb-3 text-sm text-red-600">Fehler: {liste.error.message}</div>}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-100 border-b border-slate-300 text-xs text-slate-600 uppercase">
            <tr>{['BANF-Nr', 'DV', 'Kurztext', 'Positionen', 'offen', 'bestellt', 'Wareneingang', 'Lieferanten', 'von', 'angelegt'].map(h => (
              <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="zettel-liste">
            {!liste.loading && zeilen.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-500">Keine BANF. Mit „➕ Neue BANF“ starten.</td></tr>}
            {zeilen.map(z => (
              <tr key={z.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{banfNr(z.id)}</td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap"><Link className="text-brand hover:underline" to={`/banf/${z.id}`}>{z.dv_nummer}</Link>{neuZettel.has(z.id) && <span className="ml-2"><NeuEtikett /></span>}</td>
                <td className="px-3 py-2"><Link to={`/banf/${z.id}`} className="hover:underline">{z.bezeichnung}</Link>{z.status === 'storniert' && <span className="ml-2 text-xs text-red-600">storniert</span>}{(z.pos_rueckfrage ?? 0) > 0 && <span className="ml-2 text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid="badge-rueckfrage">⏳ {z.pos_rueckfrage} beim Chef</span>}</td>
                <td className="px-3 py-2">{z.pos_gesamt ?? 0}</td>
                <td className="px-3 py-2 text-amber-700">{z.pos_offen ?? 0}</td>
                <td className="px-3 py-2 text-sky-700">{z.pos_bestellt ?? 0}</td>
                <td className="px-3 py-2 text-emerald-700">{z.pos_geliefert ?? 0}</td>
                <td className="px-3 py-2">{z.lieferanten ?? '—'}</td>
                <td className="px-3 py-2">{z.ersteller ?? '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDatum(z.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
